import { useEffect, useState } from 'react';
import { useAreasLoadedSelector, useAreasSelector, useCollectionsLoadedSelector, useCollectionsSelector, useUserSettings, } from '@/store/selectors';
import { useAreaNameFromUrl, useCollectionNameFromUrl } from '@/routing/hooks';
import { useTenantSettings } from '@/auth/hooks/use-tenant-settings';
/**
 * Find area by name for a specific collection
 * - validate inputs
 * - Log warnings for invalid areas
 * - Returns undefined if not found (graceful degradation)
 */
export const findArea = ({ areaName, areas, collection, }) => {
    if (!areaName || !(areas === null || areas === void 0 ? void 0 : areas.length) || !(collection === null || collection === void 0 ? void 0 : collection.id)) {
        return undefined;
    }
    const foundArea = areas.find(({ name, collectionId, }) => name === areaName && collectionId === collection.id);
    if (!foundArea) {
        console.warn(`[findArea] Area "${areaName}" not found for collection "${collection.collection}"`, {
            availableAreas: areas
                .filter((a) => a.collectionId === collection.id)
                .map((a) => a.name),
            hint: 'Area may not exist for this collection or URL has typo',
        });
    }
    return foundArea;
};
/**
 * Find and validate a collection by trying multiple fallback values
 * @internal - Exported for testing only
 */
export const findValidCollection = (collectionNames, collections) => {
    for (const name of collectionNames) {
        if (name) {
            const found = collections.find((c) => c.collection === name);
            if (found) {
                return found;
            }
            console.warn(`[useSetDefaultCollectionAndArea] Default collection "${name}" not found`, { availableCollections: collections.map((c) => c.collection) });
        }
    }
    return undefined;
};
/**
 * Find and validate an area by trying multiple fallback values
 * @internal - Exported for testing only
 */
export const findValidArea = (areaNames, areas, collection) => {
    for (const name of areaNames) {
        if (name) {
            const found = findArea({ areaName: name, areas, collection });
            if (found) {
                return found;
            }
        }
    }
    return undefined;
};
/**
 * Hook to set default collection and area for context selectors
 */
export function useSetDefaultCollectionAndArea({ setSelectedCollection, setSelectedArea, setSelectedAreaName, }) {
    var _a;
    const areasLoaded = useAreasLoadedSelector();
    const areas = useAreasSelector();
    const collectionsLoaded = useCollectionsLoadedSelector();
    const collections = useCollectionsSelector();
    const userSettings = useUserSettings();
    const tenantSettings = useTenantSettings();
    const urlCollectionName = useCollectionNameFromUrl();
    const urlAreaName = useAreaNameFromUrl();
    const [defaultLoaded, setDefaultLoaded] = useState(false);
    const { defaultCollection: userDefaultCollection, defaultArea: userDefaultArea } = userSettings || {};
    const { defaultCollection: tenantDefaultCollection, defaultArea: tenantDefaultArea } = tenantSettings || {};
    // Set initial selected collection and area only once on mount
    useEffect(() => {
        if (defaultLoaded)
            return;
        if (!collectionsLoaded || !areasLoaded)
            return;
        // Step 1: Find collection (URL → user default → tenant default)
        const collectionFound = findValidCollection([urlCollectionName, userDefaultCollection, tenantDefaultCollection], collections);
        if (collectionFound) {
            setSelectedCollection(collectionFound);
        }
        // Step 2: Find area (URL → user default → tenant default), only if collection exists
        if (collectionFound) {
            const areaFound = findValidArea([urlAreaName, userDefaultArea, tenantDefaultArea], areas, collectionFound);
            if (areaFound) {
                setSelectedAreaName(areaFound.name);
                setSelectedArea(areaFound);
            }
        }
        setDefaultLoaded(true);
    }, [
        areasLoaded,
        areas,
        collectionsLoaded,
        collections,
        defaultLoaded,
        urlAreaName,
        urlCollectionName,
        setSelectedAreaName,
        setSelectedArea,
        setSelectedCollection,
        userDefaultCollection,
        userDefaultArea,
        tenantDefaultCollection,
        tenantDefaultArea,
    ]);
    // Always return current validated defaults (recalculates when settings change)
    const validatedDefaultCollectionObj = findValidCollection([userDefaultCollection, tenantDefaultCollection], collections);
    const validatedDefaultCollection = validatedDefaultCollectionObj === null || validatedDefaultCollectionObj === void 0 ? void 0 : validatedDefaultCollectionObj.collection;
    const validatedDefaultArea = validatedDefaultCollectionObj
        ? (_a = findValidArea([userDefaultArea, tenantDefaultArea], areas, validatedDefaultCollectionObj)) === null || _a === void 0 ? void 0 : _a.name
        : undefined;
    return {
        defaultLoaded,
        defaultCollection: validatedDefaultCollection,
        defaultArea: validatedDefaultArea,
    };
}
export function hasArea(areas, area) {
    if (!area || !areas.length)
        return false;
    return areas.some((a) => a.id === (area === null || area === void 0 ? void 0 : area.id));
}
